/**
 * Where a page and its evidence boxes are drawn, and how a scanned page is
 * turned (engine W15, T15.17, T15.18; spec FR-E15 as amended 2026-10-15,
 * FR-E41) — pure arithmetic, so the canvas code stays thin and the turning is
 * tested without a browser.
 *
 * **The units.** A box is `[x0, top, x1, bottom]` in integer hundredths of a
 * PDF point from the page's top-left (C-E6). The extraction's `pages[]`
 * (T15.17) gives each page's size in the same units — the page the profile
 * read — so a box is placed on the drawn page by proportion, whatever drew it:
 * pdf.js at its own scale, or an image at its natural size.
 *
 * **A scan is read upright and straight, and its boxes stay there** (W14; the
 * owner, 2026-10-15: *"W15's viewer turns them"*). The static rung's `scan`
 * report says how, per page — conventions read from the kernel, not guessed:
 *
 * - **`turned`** — degrees the scan was turned **counter-clockwise** to read it
 *   upright: 0, 90, 180 or 270 (`scanned/read.py`: `PageReport.turned`, and
 *   `_attempt`'s `np.rot90(image, turns)`, which turns counter-clockwise; a page
 *   scanned a quarter-turn counter-clockwise reports 270 —
 *   `test_a_page_scanned_turned_is_read_upright`). A turned page's sides swap.
 * - **`deskew:<n>`** in the report's `steps` — the skew the reader measured, in
 *   hundredths of a degree, **counter-clockwise positive** (`scanned/quality.py`:
 *   `Quality.skew`; the fixtures' `skewed` recipe turns a page 3° counter-
 *   clockwise and logs `deskew:300`). The reader straightened the upright page by
 *   turning it n/100° clockwise about its centre, on a canvas of the same size
 *   (`scanned/preprocess.py`: `straighten`, `rotate(gray, -skew / 100)`).
 *   `deskew_refused:<n>` was tried and not applied.
 *
 * Both are turns about the page's centre, so together they are **one** rotation:
 * `turned`° counter-clockwise, then n/100° clockwise — in CSS, where a positive
 * angle is clockwise, `rotate(n/100 − turned deg)`. The viewer draws the page as
 * it lies (pdf.js's canvas, or the image) centred in a frame the size of the
 * upright page, turns it by that rotation, and draws every box axis-aligned in
 * the frame: on the printed figure.
 */

import type { Box, ExtractionPage, Rung, ScanPage } from './extraction-shapes'

/** How a page was turned upright and straightened before it was read. */
export interface PageTurn {
  /** Degrees the scan was turned counter-clockwise to read it upright: 0, 90, 180, 270. */
  turned: number
  /** The skew straightened, hundredths of a degree, counter-clockwise positive; 0 for none. */
  deskew: number
}

/** A page read as it lies: a digital PDF, a searchable scan's own layer, a page that needed no turn. */
export const UPRIGHT: PageTurn = { turned: 0, deskew: 0 }

/** The scan report of the reading that was kept — the static rung's; empty for a digital file. */
export const scanOf = (rungs: readonly Rung[]): ScanPage[] => rungs.find((r) => r.scan && r.scan.length > 0)?.scan ?? []

/** How page `page` (1-based) was turned, from the scan report; upright when the report does not name it. */
export function pageTurnOf(scan: readonly ScanPage[], page: number): PageTurn {
  const found = scan.find((p) => p.page === page)
  return found ? { turned: found.turned, deskew: found.deskew } : UPRIGHT
}

/** Whether the turn is a quarter one way or the other — the page's sides swap. */
export const sidesSwap = (turn: PageTurn): boolean => turn.turned % 180 !== 0

/**
 * Clockwise degrees (CSS's sense) that turn the page as it lies into the page
 * its boxes are on: `turned`° counter-clockwise, then the skew straightened —
 * in `(-180, 180]`.
 */
export function rotationOf(turn: PageTurn): number {
  const degrees = (((turn.deskew / 100 - turn.turned) % 360) + 360) % 360
  return degrees > 180 ? degrees - 360 : degrees
}

/** CSS pixels per point that fit the page to the width it has, within reason. */
export function fitScale(available: number, pageWidth: number): number {
  if (!(pageWidth > 0)) return 1
  const width = available > 0 ? available : 640
  return Math.min(2.5, Math.max(0.5, width / pageWidth))
}

/** A box in hundredths of a point, as CSS pixels at `scale` CSS pixels per point. */
export function boxStyle(box: Box, scale: number): { left: number; top: number; width: number; height: number } {
  const [x0, top, x1, bottom] = box
  const px = (hundredths: number) => (hundredths / 100) * scale
  return { left: px(Math.min(x0, x1)), top: px(Math.min(top, bottom)), width: px(Math.abs(x1 - x0)), height: px(Math.abs(bottom - top)) }
}

/** A page's size, `{width, height}`. */
export interface Size {
  width: number
  height: number
}

/**
 * The size, in hundredths of a point, of the page a PDF page's boxes are on
 * when its extraction carries no `pages[]` (read before kernel 0.15): pdf.js's
 * size in points, its sides swapped when a scan was read turned.
 */
export function uprightSizeOf(points: Size, turn: PageTurn): Size {
  const width = points.width * 100
  const height = points.height * 100
  return sidesSwap(turn) ? { width: height, height: width } : { width, height }
}

/** The size of page `number` from `pages[]`, or `null`. */
export const pageSizeOf = (pages: readonly ExtractionPage[], number: number): Size | null => {
  const found = pages.find((p) => p.number === number)
  return found ? { width: found.width, height: found.height } : null
}

/** A page laid out on screen. */
export interface PageLayout {
  /** The page the boxes are on — upright and straight — in CSS pixels. */
  frame: Size
  /** The page as it lies (the PDF's canvas, the image), in CSS pixels: centred in the frame, then turned. */
  source: { left: number; top: number; width: number; height: number }
  /** Clockwise degrees the source is turned about its centre (`rotationOf`). */
  rotate: number
  /** CSS pixels per point of the frame: a box is drawn at `boxStyle(box, scale)`. */
  scale: number
}

/**
 * Lay a page out: `page` is the size its boxes are on, in hundredths of a point
 * (`pages[]`, or `uprightSizeOf`); `turn` how it was read; `available` the CSS
 * width it may take. The frame is the page the boxes are on; the source is the
 * page as it lies — its sides swapped back where the turn swapped them — so,
 * turned, it fills the frame.
 */
export function pageLayout(page: Size, turn: PageTurn, available: number): PageLayout {
  const scale = fitScale(available, page.width / 100)
  const frame = { width: (page.width / 100) * scale, height: (page.height / 100) * scale }
  const [width, height] = sidesSwap(turn) ? [frame.height, frame.width] : [frame.width, frame.height]
  return {
    frame,
    source: { left: (frame.width - width) / 2, top: (frame.height - height) / 2, width, height },
    rotate: rotationOf(turn),
    scale,
  }
}

/** The style that places and turns the page as it lies — what `toFrame` computes. */
export function sourceStyle(layout: PageLayout): { left: number; top: number; width: number; height: number; transform?: string } {
  const { left, top, width, height } = layout.source
  return layout.rotate === 0 ? { left, top, width, height } : { left, top, width, height, transform: `rotate(${layout.rotate}deg)` }
}

/**
 * Where a point of the page as it lies — CSS pixels from the source's own
 * top-left — appears in the frame once the source is turned: the arithmetic of
 * `sourceStyle`'s transform (CSS turns about the element's centre, clockwise
 * for a positive angle, with y pointing down).
 */
export function toFrame(layout: PageLayout, x: number, y: number): [number, number] {
  const { source, rotate } = layout
  const dx = x - source.width / 2
  const dy = y - source.height / 2
  const r = (rotate * Math.PI) / 180
  const cos = Math.cos(r)
  const sin = Math.sin(r)
  return [source.left + source.width / 2 + dx * cos - dy * sin, source.top + source.height / 2 + dx * sin + dy * cos]
}
